import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { ActivatedRoute, RouterModule } from '@angular/router';
import { ProctoringService } from '@edtech-ui/core/services/proctoring-dashboard/proctoring.service';
import { ToastrService } from 'ngx-toastr';
import { catchError, finalize, of, Subscription, tap } from 'rxjs';
import { ProctoringSessionReportByIds } from '@edtech-ui/core/models/proctoring-dashboard-models/getproctoring-session-report-by-id';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';


@Component({
	selector: 'app-proctoring-candidate-detail-page',
	standalone: true,
	imports: [CommonModule, RouterModule, FormsModule, MatProgressSpinnerModule, ReactiveFormsModule],
	templateUrl: './proctoring-candidate-detail-page.component.html',
	styleUrl: './proctoring-candidate-detail-page.component.scss'
})
export class ProctoringCandidateDetailPageComponent implements OnInit, OnDestroy {

	isLoading = false;
	subscription = new Subscription();
	sessionId = '';
	sessionData: ProctoringSessionReportByIds | null = null;
	configurationData: any = null;
	isDownloading = false;
	previewSnapshotUrl: string | null = null;


	constructor(
		private route: ActivatedRoute,
		private proctoringService: ProctoringService,
		private toastr: ToastrService
	) {
	}


	ngOnInit(): void {
		this.sessionId = this.route.snapshot.paramMap.get('sessionId') || '';
		if (this.sessionId) {
			this.getProctoringSessionsById();
		}
	}


	getProctoringSessionsById() {
		this.isLoading = true;
		this.subscription.add(
			this.proctoringService.getProctoringSessionsById(this.sessionId).pipe(
				tap(response => {
					if (response.success) {
						this.sessionData = response.data;
					} else {
						this.toastr.error(response.message);
					}
				}),
				finalize(() => {
					this.isLoading = false;
				}),
				catchError(() => {
					return of(null);
				})
			).subscribe()
		);
	}


	get initials(): string {
		if (!this.sessionData?.summary?.candidateName) return '';
		return this.sessionData.summary.candidateName
			.split(' ')
			.map(x => x[0])
			.join('')
			.toUpperCase();
	}



	async downloadReport() {
		const element = document.getElementById('candidate-report-content');
		if (!element) {
			this.toastr.error('Report content not found.');
			return;
		}

		this.isDownloading = true;
		this.toastr.info('Generating PDF report...', 'Please wait');
		try {
			const images = Array.from(element.querySelectorAll('img'));
			const originalSrcs = new Map<HTMLImageElement, string>();

			const blobToBase64 = (blob: Blob): Promise<string> =>
				new Promise((resolve, reject) => {
					const reader = new FileReader();
					reader.onloadend = () => resolve(reader.result as string);
					reader.onerror = reject;
					reader.readAsDataURL(blob);
				});

			await Promise.all(
				images.map(async (img) => {
					if (!img.src || img.src.startsWith('data:')) return;

					try {
						originalSrcs.set(img, img.src);
						const response = await fetch(img.src, {
							cache: 'force-cache'
						});
						const blob = await response.blob();
						img.src = await blobToBase64(blob);
					} catch (e) {
						console.error(e);
					}
				}));
			await new Promise(resolve => setTimeout(resolve, 100));

			const wrapper = document.createElement('div');
			wrapper.style.position = 'absolute';
			wrapper.style.left = '-9999px';
			wrapper.style.top = '0';
			wrapper.style.width = '1200px';
			wrapper.style.background = '#f8fafc';

			const clone = element.cloneNode(true) as HTMLElement;
			wrapper.appendChild(clone);
			document.body.appendChild(wrapper);

			const pdf = new jsPDF('p', 'mm', 'a4');
			const pdfWidth = pdf.internal.pageSize.getWidth();
			const pageHeight = pdf.internal.pageSize.getHeight();
			const margin = 10;
			const contentWidth = pdfWidth - (margin * 2);
			let currentY = margin;

			const addSectionToPdf = async (elementId: string) => {
				const el = clone.querySelector(`#${elementId}`) as HTMLElement;
				if (!el) return;

				const canvas = await html2canvas(el, {
					scale: 1.3,
					useCORS: true,
					backgroundColor: '#ffffff',
					windowWidth: 1200
				});

				const imgData = canvas.toDataURL('image/png', 0.85);
				const imgProps = pdf.getImageProperties(imgData);
				const imgHeight = (imgProps.height * contentWidth) / imgProps.width;

				if (currentY + imgHeight > pageHeight - margin) {
					pdf.addPage();
					currentY = margin;
				}

				pdf.addImage(imgData, 'JPEG', margin, currentY, contentWidth, imgHeight);
				currentY += imgHeight + 5;
			};

			await addSectionToPdf('pdf-summary-section');
			await addSectionToPdf('pdf-evidence-header');

			if (!this.violationsWithEvidence?.length) {
				await addSectionToPdf('pdf-evidence-empty');
			} else {
				for (let i = 0; i < this.evidencePages.length; i++) {
					await addSectionToPdf(`pdf-evidence-page-${i}`);
				}
			}

			document.body.removeChild(wrapper);
			originalSrcs.forEach((src, img) => { img.src = src });

			const fileName = `${this.sessionData?.summary?.candidateName || 'Candidate'}_Report.pdf`;
			pdf.save(fileName);
			this.toastr.success('Report downloaded successfully!');
		} catch (error) {
			this.toastr.error('Failed to generate PDF report.');
		} finally {
			this.isDownloading = false;
		}
	}

	get integrityScore(): number {
		const summary = this.sessionData?.summary;
		if (!summary) return 0;

		const status = summary.sessionStatus?.toLowerCase() || '';
		if (summary.totalViolations === 0 && (status === 'abandoned' || status === 'abandon')) {
			return 0;
		}

		return summary.integrityScore ?? 0;
	}

	get hasIntegrityScore(): boolean {
		const summary = this.sessionData?.summary;
		if (!summary) return false;

		const status = summary.sessionStatus?.toLowerCase() || '';
		if (summary.totalViolations === 0 && (status === 'abandoned' || status === 'abandon')) {
			return true;
		}
		return summary.integrityScore !== null && summary.integrityScore !== undefined;
	}


	get violationsWithEvidence(): any[] {
		return this.sessionData?.violations?.filter(v => v.evidenceUrl) || [];
	}

	get integrityScoreDashArray(): string {
		return `${this.integrityScore}, 100`;
	}

	get integrityStatus(): string {
		if (!this.hasIntegrityScore) return '-';
		if (this.integrityScore >= 90) return 'Excellent';
		if (this.integrityScore >= 75) return 'Good';
		if (this.integrityScore >= 50) return 'Average';
		return 'Poor';
	}

	formatDuration(seconds: number | null | undefined): string {
		if (seconds === null || seconds === undefined) return '-';
		if (seconds <= 60) return `${seconds} sec`;
		const mins = Math.floor(seconds / 60);
		const secs = seconds % 60;
		return secs > 0 ? `${mins} min ${secs} sec` : `${mins} min`;
	}

	get integrityColorClass(): string {
		if (!this.hasIntegrityScore) return 'gray';
		if (this.integrityScore >= 90) return 'green';
		if (this.integrityScore >= 75) return 'blue';
		if (this.integrityScore >= 50) return 'orange';
		return 'red';
	}

	get integrityStrokeColor(): string {
		if (!this.hasIntegrityScore) return '#94a3b8';
		if (this.integrityScore >= 90) return '#16a34a';
		if (this.integrityScore >= 75) return '#2563eb';
		if (this.integrityScore >= 50) return '#f59e0b';
		return '#dc2626';
	}

	get integrityTextColorClass(): string {
		if (!this.hasIntegrityScore) return 'text-muted';
		if (this.integrityScore >= 90) return 'text-success';
		if (this.integrityScore >= 75) return 'text-primary';
		if (this.integrityScore >= 50) return 'text-warning';
		return 'text-danger';
	}


	openImageInNewTab(url: string | undefined): void {
		if (url) {
			window.open(url, '_blank');
		}
	}


	openSnapshotPreview(url: string | undefined): void {
		if (url) {
			this.previewSnapshotUrl = url;
		}
	}


	closeSnapshotPreview(): void {
		this.previewSnapshotUrl = null;
	}


	get evidencePages(): any[][] {
		const pages: any[][] = [];
		for (let i = 0; i < this.violationsWithEvidence.length; i += 9) {
			pages.push(this.violationsWithEvidence.slice(i, i + 9));
		}
		return pages;
	}

	trackByPage(index: number, page: any[]): number {
		return index;
	}

	trackByViolation(index: number, violation: any): string {
		return violation.evidenceUrl || index.toString();
	}


	ngOnDestroy(): void {
		this.subscription.unsubscribe();
	}
}